import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { match, P } from "ts-pattern";
import { useGetInvite } from "@/hooks/queries/use-get-invite";
import { RegisterForm } from "../forms/register-form";
import { ThemeSwitcher } from "../theme-switcher";
import { Button } from "../ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "../ui/card";

type RegistrationState = "idle" | "error" | "success";

export function InvitePage() {
	const [registrationState, setRegistrationState] =
		useState<RegistrationState>("idle");
	const navigate = useNavigate();
	const { inviteCode } = useParams();
	const inviteQuery = useGetInvite(inviteCode);

	// Handle redirect for invalid invite
	useEffect(() => {
		if (inviteQuery.isFetched && (inviteQuery.isError || !inviteQuery.data)) {
			navigate("/login");
		}

		if (inviteQuery.data) {
			// Check if invite is internal type - redirect to register page
			if (inviteQuery.data.type === "internal") {
				navigate(`/register/${inviteQuery.data.invite_code}`);
				return;
			}

			if (inviteQuery.data.accepted) {
				setRegistrationState("error");
			}

			const now = new Date();
			const expires = new Date(inviteQuery.data.expires);
			if (now > expires) {
				setRegistrationState("error");
			}
		}
	}, [inviteQuery.isFetched, inviteQuery.isError, inviteQuery.data, navigate]);

	return (
		<>
			<div className="fixed top-4 right-4 z-50">
				<ThemeSwitcher />
			</div>
			{match({ query: inviteQuery, registration: registrationState })
				.with({ query: { isLoading: true } }, () => (
					<p>Loading Invitation...</p>
				))
				.with({ registration: "success" }, () => <RegisterSuccessCard />)
				.with({ registration: "error" }, () => <RegisterErrorCard />)
				.with({ query: { isSuccess: true, data: P.not(P.nullish) } }, () => (
					<RegisterForm
						onSuccess={() => setRegistrationState("success")}
						onError={() => setRegistrationState("error")}
						invite={inviteQuery.data}
					/>
				))
				.otherwise(() => (
					<Navigate to="/login" />
				))}
		</>
	);
}

function RegisterErrorCard() {
	return (
		<Card className="max-w-1/2">
			<CardHeader>
				<h1 className="text-xl">Registration Error</h1>
			</CardHeader>
			<CardContent>
				<h2 className="text-lg">
					We ran into an unexpected error trying to get you registered
				</h2>
				<p>
					Please try again. If the issue persists, send us an email at:{" "}
					<a
						className="underline text-blue-500"
						href="mailto:reachout@indivisibleeldorado.org"
					>
						reachout@indivisibleeldorado.org
					</a>
				</p>
			</CardContent>
			<CardFooter>
				<Button onClick={() => window.location.reload()}>Retry</Button>
			</CardFooter>
		</Card>
	);
}

function RegisterSuccessCard() {
	return (
		<Card className="max-w-1/2">
			<CardHeader>
				<h1 className="text-xl">Registration Complete</h1>
			</CardHeader>
			<CardContent className="space-y-2">
				<h2 className="text-lg">
					Thank you for joining INDIVISIBLE El Dorado!
				</h2>
				<p>
					We will begin the process of onboarding you to the relevant chats and
					email lists. If you do not receive invitations via phone or email
					within the next 72 hours please reach out to us at:{" "}
					<a
						className="underline text-blue-500"
						href="mailto:reachout@indivisibleeldorado.org"
					>
						reachout@indivisibleeldorado.org
					</a>
				</p>
				<p>You can safely close this tab or navigate away now.</p>
			</CardContent>
		</Card>
	);
}
